import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  TrendingDown as TrendingDownIcon,
  Language as OnlinePaymentIcon,
  Storefront as InPersonPaymentIcon
} from '@mui/icons-material';

const SchoolFeesReport = ({ data, formatCurrency }) => {
  const [cancellationFilter, setCancellationFilter] = useState('active');
  const [channelFilter, setChannelFilter] = useState('all');

  const periodPayments = data?.periodPayments ?? [];
  const cancelledPaymentsInPeriod = data?.cancelledPaymentsInPeriod ?? [];

  const filteredPeriodPayments = useMemo(() => {
    let rows =
      cancellationFilter === 'cancelled'
        ? cancelledPaymentsInPeriod
        : cancellationFilter === 'pending'
          ? periodPayments.filter((r) => r.isPendingCancellation)
          : cancellationFilter === 'all'
            ? [...periodPayments, ...cancelledPaymentsInPeriod]
            : periodPayments;

    if (channelFilter !== 'all') {
      rows = rows.filter((r) => r.paymentChannel === channelFilter);
    }
    return rows;
  }, [periodPayments, cancelledPaymentsInPeriod, cancellationFilter, channelFilter]);

  if (!data) return null;

  const { summary, breakdown, students } = data;

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '—';
    const parts = String(dateStr).split('T')[0].split('-');
    if (parts.length < 3) return dateStr;
    const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    return d.toLocaleDateString();
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        School Fees Report
      </Typography>
      
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Students
                  </Typography>
                  <Typography variant="h4" component="div">
                    {summary.totalStudents}
                  </Typography>
                </Box>
                <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Fees Due
                  </Typography>
                  <Typography variant="h4" component="div" color="primary">
                    {formatCurrency(summary.totalFeesDue)}
                  </Typography>
                </Box>
                <AccountBalanceIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Amount Collected (All Time)
                  </Typography>
                  <Typography variant="h4" component="div" color="success.main">
                    {formatCurrency(summary.totalAmountPaid)}
                  </Typography>
                  {summary.totalAmountPaidInPeriod > 0 && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      {formatCurrency(summary.totalAmountPaidInPeriod)} in period
                    </Typography>
                  )}
                  {(summary.totalOnlineFeePaidAllTime || 0) > 0 ||
                  (summary.totalInPersonFeePaidAllTime || 0) > 0 ? (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Fee payments (excl. discounts)
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.25 }}>
                        <OnlinePaymentIcon sx={{ fontSize: 14, color: 'info.main' }} />
                        <Typography variant="caption" color="textSecondary">
                          Online: {formatCurrency(summary.totalOnlineFeePaidAllTime || 0)}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <InPersonPaymentIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="textSecondary">
                          In person / other:{' '}
                          {formatCurrency(summary.totalInPersonFeePaidAllTime || 0)}
                        </Typography>
                      </Box>
                    </Box>
                  ) : null}
                  {(summary.totalCancelledInPeriod || 0) > 0 && (
                    <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                      Cancelled (excluded): {formatCurrency(summary.totalCancelledInPeriod)}
                    </Typography>
                  )}
                </Box>
                <ReceiptIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Outstanding
                  </Typography>
                  <Typography variant="h4" component="div" color="error.main">
                    {formatCurrency(summary.totalOutstanding)}
                  </Typography>
                </Box>
                <TrendingUpIcon color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Discounts Given
                  </Typography>
                  <Typography variant="h4" component="div" color="warning.main">
                    {formatCurrency(summary.totalDiscounts || 0)}
                  </Typography>
                  {(summary.totalDiscountsInPeriod || 0) > 0 && (
                    <Typography variant="caption" color="textSecondary">
                      {formatCurrency(summary.totalDiscountsInPeriod || 0)} in period
                    </Typography>
                  )}
                </Box>
                <TrendingDownIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Period Summary */}
      {summary.period && (
        <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Period: {new Date(summary.period.start).toLocaleDateString()} - {new Date(summary.period.end).toLocaleDateString()}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Amount Collected in Period
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {formatCurrency(summary.totalAmountPaidInPeriod)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Period Collection Rate
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {summary.periodCollectionRate ? summary.periodCollectionRate.toFixed(1) : '0.0'}%
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Discounts Given in Period
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {formatCurrency(summary.totalDiscountsInPeriod || 0)}
                </Typography>
              </Grid>
              {(summary.totalOnlineFeePaidInPeriod || 0) > 0 ||
              (summary.totalInPersonFeePaidInPeriod || 0) > 0 ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Online fee payments (period)
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(summary.totalOnlineFeePaidInPeriod || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      In person / other fee payments (period)
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(summary.totalInPersonFeePaidInPeriod || 0)}
                    </Typography>
                  </Grid>
                </>
              ) : null}
              {(summary.totalCancelledInPeriod || 0) > 0 && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Cancelled payments (period, excluded)
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="error.light">
                    {formatCurrency(summary.totalCancelledInPeriod)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Collection Rate */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Overall Collection Rate (All Time)
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <LinearProgress 
              variant="determinate" 
              value={summary.collectionRate} 
              sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
            />
            <Typography variant="h6" color="primary">
              {summary.collectionRate.toFixed(1)}%
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {summary.paidStudents} of {summary.totalStudents} students have paid their fees
          </Typography>
        </CardContent>
      </Card>

      {/* Breakdown by Class */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Breakdown by Class
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Class</strong></TableCell>
                  <TableCell align="right"><strong>Students</strong></TableCell>
                  <TableCell align="right"><strong>Fees Due</strong></TableCell>
                  <TableCell align="right"><strong>Collected (All)</strong></TableCell>
                  <TableCell align="right"><strong>Collected (Period)</strong></TableCell>
                  <TableCell align="right"><strong>Outstanding</strong></TableCell>
                  <TableCell align="right"><strong>Collection Rate</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(breakdown.byClass).map(([className, classData]) => {
                  // Calculate period payments for this class
                  const classStudents = data.students.filter(s => s.class === className);
                  const periodPayments = classStudents.reduce((sum, s) => sum + (s.amountPaidInPeriod || 0), 0);
                  
                  return (
                    <TableRow key={className}>
                      <TableCell>{className}</TableCell>
                      <TableCell align="right">{classData.totalStudents}</TableCell>
                      <TableCell align="right">{formatCurrency(classData.totalFeesDue)}</TableCell>
                      <TableCell align="right">{formatCurrency(classData.totalAmountPaid)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="primary.main" fontWeight="bold">
                          {formatCurrency(periodPayments)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(classData.totalOutstanding)}</TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={`${((classData.totalAmountPaid / classData.totalFeesDue) * 100).toFixed(1)}%`}
                          color={classData.totalAmountPaid / classData.totalFeesDue >= 0.8 ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Breakdown by Term */}
      {breakdown.byTerm && Object.keys(breakdown.byTerm).length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Breakdown by Term
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Term</strong></TableCell>
                    <TableCell align="right"><strong>Students</strong></TableCell>
                    <TableCell align="right"><strong>Fees Due</strong></TableCell>
                    <TableCell align="right"><strong>Collected (All)</strong></TableCell>
                    <TableCell align="right"><strong>Collected (Period)</strong></TableCell>
                    <TableCell align="right"><strong>Outstanding</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(breakdown.byTerm).map(([term, data]) => (
                    <TableRow key={term}>
                      <TableCell>{term}</TableCell>
                      <TableCell align="right">{data.totalStudents}</TableCell>
                      <TableCell align="right">{formatCurrency(data.totalFeesDue)}</TableCell>
                      <TableCell align="right">{formatCurrency(data.totalAmountPaid)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="primary.main" fontWeight="bold">
                          {formatCurrency(data.totalAmountPaidInPeriod)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(data.totalOutstanding)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Payment Status Breakdown */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payment Status Breakdown
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="success.main" variant="h4">
                    {breakdown.byPaymentStatus.fullyPaid.count}
                  </Typography>
                  <Typography color="textSecondary">
                    Fully Paid Students
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    {formatCurrency(breakdown.byPaymentStatus.fullyPaid.totalPaid)} collected
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="warning.main" variant="h4">
                    {breakdown.byPaymentStatus.partiallyPaid.count}
                  </Typography>
                  <Typography color="textSecondary">
                    Partially Paid Students
                  </Typography>
                  <Typography variant="body2" color="warning.main">
                    {formatCurrency(breakdown.byPaymentStatus.partiallyPaid.totalOutstanding)} outstanding
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="error.main" variant="h4">
                    {breakdown.byPaymentStatus.notPaid.count}
                  </Typography>
                  <Typography color="textSecondary">
                    Not Paid Students
                  </Typography>
                  <Typography variant="body2" color="error.main">
                    {formatCurrency(breakdown.byPaymentStatus.notPaid.totalOutstanding)} outstanding
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Period payment transactions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            School Fees Payments (Period)
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Active totals exclude cancelled payments. Online (period):{' '}
            {formatCurrency(summary.totalOnlineFeePaidInPeriod || 0)} · In person (period):{' '}
            {formatCurrency(summary.totalInPersonFeePaidInPeriod || 0)}
            {(summary.totalCancelledInPeriod || 0) > 0 && (
              <> · Cancelled (excluded): {formatCurrency(summary.totalCancelledInPeriod)}</>
            )}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="acct-fees-status-filter">Payment status</InputLabel>
              <Select
                labelId="acct-fees-status-filter"
                label="Payment status"
                value={cancellationFilter}
                onChange={(e) => setCancellationFilter(e.target.value)}
              >
                <MenuItem value="active">Active (in totals)</MenuItem>
                <MenuItem value="pending">Pending cancellation</MenuItem>
                <MenuItem value="cancelled">Cancelled only</MenuItem>
                <MenuItem value="all">All statuses</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="acct-fees-channel-filter">Channel</InputLabel>
              <Select
                labelId="acct-fees-channel-filter"
                label="Channel"
                value={channelFilter}
                onChange={(e) => setChannelFilter(e.target.value)}
              >
                <MenuItem value="all">All channels</MenuItem>
                <MenuItem value="in_person">In person only</MenuItem>
                <MenuItem value="online">Online only</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>No.</strong></TableCell>
                  <TableCell><strong>Student</strong></TableCell>
                  <TableCell><strong>Class</strong></TableCell>
                  <TableCell><strong>Channel</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="right"><strong>Amount</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Received by</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPeriodPayments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="textSecondary" variant="body2">
                        No payments match the selected filters for this period.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPeriodPayments.map((row, index) => (
                    <TableRow
                      key={`${row.studentId}-${row.date}-${index}`}
                      sx={{
                        ...(row.isCancelled ? { textDecoration: 'line-through', opacity: 0.65 } : {}),
                        ...(row.isPendingCancellation ? { backgroundColor: '#fff8e1' } : {}),
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{row.studentName}</TableCell>
                      <TableCell>{row.class}</TableCell>
                      <TableCell>{row.paymentChannel === 'online' ? 'Online' : 'In person'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.statusLabel || 'Active'}
                          color={
                            row.isCancelled ? 'default' : row.isPendingCancellation ? 'warning' : 'success'
                          }
                          variant={row.isCancelled ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                      <TableCell>{formatDisplayDate(row.date)}</TableCell>
                      <TableCell>{row.paidTo || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {cancellationFilter === 'active' && cancelledPaymentsInPeriod.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: 'text.secondary' }}>
                Cancelled payments (not included in period totals above)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>No.</strong></TableCell>
                      <TableCell><strong>Student</strong></TableCell>
                      <TableCell><strong>Class</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Received by</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cancelledPaymentsInPeriod.map((row, index) => (
                      <TableRow
                        key={`cancelled-${row.studentId}-${row.date}-${index}`}
                        sx={{ textDecoration: 'line-through', opacity: 0.65 }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{row.studentName}</TableCell>
                        <TableCell>{row.class}</TableCell>
                        <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                        <TableCell>{formatDisplayDate(row.date)}</TableCell>
                        <TableCell>{row.paidTo || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>

      {/* Top Outstanding Students */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Top Outstanding Students
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Student</strong></TableCell>
                  <TableCell><strong>Class</strong></TableCell>
                  <TableCell align="right"><strong>Fees Due</strong></TableCell>
                  <TableCell align="right"><strong>Paid</strong></TableCell>
                  <TableCell align="right"><strong>Outstanding</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students
                  .filter(student => student.outstandingBalance > 0)
                  .sort((a, b) => b.outstandingBalance - a.outstandingBalance)
                  .slice(0, 10)
                  .map((student, index) => (
                    <TableRow key={student.studentId}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {student.studentName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {student.studentIdNumber}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{student.class}</TableCell>
                      <TableCell align="right">{formatCurrency(student.totalFeesDue)}</TableCell>
                      <TableCell align="right">{formatCurrency(student.amountPaid)}</TableCell>
                      <TableCell align="right">
                        <Typography color="error.main" fontWeight="bold">
                          {formatCurrency(student.outstandingBalance)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={student.isFullyPaid ? "Paid" : "Outstanding"}
                          color={student.isFullyPaid ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SchoolFeesReport; 